import { apiGet, apiPost, apiPostMultipart } from '../helpers/ApiHelper';
import Auth from 'j-toker';
import { toDate, toFriendlyDateString, toTimeString } from 'helpers/Formatter';
import { unreadMessages } from 'actions/dashboardActions';
import * as notifications from './notificationActions';

// Messages
function formatMessage(message) {
  return {
    message_id: message.id,
    marked_read_by: message.marked_read_by,
    author_id: message.author_id,
    author_type: message.author_type,
    author_name: message.author_name,
    author_avatar: message.author_avatar,
    text: message.message,
    seen_by: message.seen_by,
    date: toDate(message.created_at),
    attachment_url: message.attachment_url,
    attachment_name: message.attachment_file_name,
    author: message.author_type,
    type: 'text',
    data: {
      text: message.message,
      author: message.author_name,
      date: toFriendlyDateString(message.created_at),
      time: toTimeString(message.created_at),
    },
  };
}

function newMessage(message) {
  return {
    type: 'NEW_MESSAGE',
    message: message,
  };
}

function requestMessages(options = { isForMoreMessages: false }) {
  let type;
  if (options.isForMoreMessages) {
    type = 'LOAD_MORE_MESSAGES';
  } else {
    type = 'LOAD_MESSAGES';
  }
  return { type };
}

export function receiveMessages(messages, options = { isForMoreMessages: false }) {
  let type;
  if (options.isForMoreMessages) {
    type = 'LOAD_MORE_MESSAGES_SUCCESS';
  } else {
    type = 'LOAD_MESSAGES_SUCCESS';
  }
  return {
    type,
    messages,
  };
}

function updateTodoMessages() {
  return {
    type: 'TODOS::UPDATE_TODO_MESSAGES',
  };
}

export function markMessagesAsRead(conversationId) {
  const route = `/chat/conversations/${conversationId}/mark_message_read`;
  return (dispatch) => {
    return dispatch({
      payload: apiGet(Auth.getApiUrl() + route)
        .then(() => {
          return;
        })
        .catch((error) => {
          dispatch(notifications.showFailure(error));
          console.error('request failed', error);
        }),
    });
  };
}

export function sendMessage(message, conversation_id, file) {
  const route = `/chat/conversations/${conversation_id}/messages`;
  return (dispatch) => {
    if (!file) {
      return dispatch({
        payload: apiPost(Auth.getApiUrl() + route, { message })
          .then((response) => {
            dispatch(newMessage(formatMessage(response)));
          })
          .catch((error) => {
            dispatch(notifications.showFailure(error));
            console.error('request failed', error);
          }),
      });
    } else {
      let formData = new FormData();
      formData.append('attachment', file);
      if (message !== null) {
        formData.append('message', message);
      }
      return dispatch({
        payload: apiPostMultipart(Auth.getApiUrl() + route, formData)
          .then((response) => {
            dispatch(newMessage(formatMessage(response)));
          })
          .catch((error) => {
            dispatch(notifications.showFailure(error));
            console.error('request failed', error);
          }),
      });
    }
  };
}

export function fetchMessages(conversation_id, options = { isForMoreMessages: false }) {
  return (dispatch, getState) => {
    const { messages } = getState().chat;
    const offset = options.isForMoreMessages ? messages.length : 0;
    dispatch(requestMessages(options));
    return {
      payload: apiGet(
        Auth.getApiUrl() + `/chat/conversations/${conversation_id}/messages?offset=${offset}`
      )
        .then((response) => {
          let formattedMessages = [];
          if (response && response.length > 0) {
            formattedMessages = response.map((message) => {
              return formatMessage(message);
            });
          }
          dispatch(receiveMessages(formattedMessages, options));
        })
        .catch((error) => {
          dispatch(notifications.showFailure(error));
          console.error('request failed', error);
        }),
    };
  };
}

// Conversations
function activeConversation(conversation) {
  return {
    type: 'ACTIVE_CONVERSATION',
    conversation,
  };
}

function requestConversations() {
  return {
    type: 'LOAD_CONVERSATIONS',
  };
}

function receiveConversations(conversations, options = { isForMoreConversations: false }) {
  let type = 'LOAD_CONVERSATIONS_SUCCESS';
  if (options.isForMoreConversations) {
    type = 'LOAD_MORE_CONVERSATIONS_SUCCESS';
  }
  return {
    type,
    conversations,
  };
}

// fetch new messages for loaded conversation
export function updateConversation(conversationId, date, chatIsOpen = false) {
  if (!window.localStorage.authHeaders) {
    return (dispatch) => {
      dispatch({
        type: 'UPDATE_CONVERSATION_FAILED',
      });
    };
  }
  return (dispatch) => {
    const timestamp = date ? (date.getTime() / 1000) | 0 : 0; // convert time to seconds since epoch
    const params = `?conversation_id=${conversationId}&timestamp=${timestamp}`;
    apiGet(Auth.getApiUrl() + `/chat/new_correspondence${params}`)
      .then((response) => {
        if (response && response.length > 0) {
          dispatch(unreadMessages(conversationId, response.length));
          for (var i = 0; i < response.length; i++) {
            dispatch(newMessage(formatMessage(response[i])));
          }
        }
      })
      .catch((error) => {
        console.error('request failed', error);
      });
  };
}

// fetch new conversations
export function updateConversations(updated_at) {
  if (!window.localStorage.authHeaders) {
    return (dispatch) => {
      dispatch({
        type: 'UPDATE_CONVERSATIONS_FAILED',
      });
    };
  }
  return (dispatch) => {
    const timestamp = (updated_at / 1000) | 0;
    const params = `?timestamp=${timestamp}`;
    return apiGet(Auth.getApiUrl() + `/chat/conversations${params}`)
      .then((response) => {
        const { conversations } = response;
        if (conversations && conversations.length > 0) {
          dispatch(receiveConversationUpdates(conversations));
        }
      })
      .catch((error) => {
        dispatch(notifications.showFailure(error));
        console.error('request failed', error);
      });
  };
}

export function receiveConversationUpdates(conversations) {
  return {
    type: 'UPDATE_CONVERSATIONS_SUCCESS',
    conversations,
  };
}

export function setActiveConversation(conversation, options = {}) {
  if (!conversation) return;

  return (dispatch) => {
    dispatch(activeConversation(conversation));
    // fetch messages for the active conversation
    dispatch(fetchMessages(conversation.id, options));
  };
}

export function fetchConversations(options = { isForMoreConversations: false }) {
  return (dispatch, getState) => {
    const { filter, search, isForMoreConversations } = options;
    const { conversations } = getState().chat;
    const offset = isForMoreConversations ? conversations.length : 0;
    const filterQueryString = filter ? `&subset=${filter}` : '';
    const searchQueryString = search ? `&query=${search}` : '';
    dispatch(requestConversations());
    return apiGet(
      Auth.getApiUrl() +
        `/chat/conversations?offset=${offset}${filterQueryString}${searchQueryString}`
    )
      .then((response) => {
        dispatch(receiveConversations(response.conversations, options));
      })
      .catch((error) => {
        console.error('request failed', error);
      });
  };
}

export function fetchConversationByCotenant(cotenantId, options = {}) {
  return (dispatch) => {
    dispatch(requestConversations());
    return apiGet(Auth.getApiUrl() + `/chat/conversation_by_cotenant/${cotenantId}`)
      .then((response) => {
        return dispatch(setActiveConversation(response.conversation, options));
      })
      .catch((error) => {
        console.error('request failed', error);
      });
  };
}

export function initalizeChat(cotenantId) {
  return (dispatch, getState) => {
    return dispatch(fetchConversations()).then(() => {
      const { conversations } = getState().chat;
      if (!conversations || conversations.length === 0) return;

      let activeConversation = conversations.find((conversation) =>
        conversation.cotenants.map((cotenant) => cotenant.cotenant_id).includes(cotenantId)
      );
      if (!activeConversation) return;

      dispatch(updateTodoMessages());
      return dispatch(setActiveConversation(activeConversation));
    });
  };
}

export function toggleChatDrawer(shouldShow) {
  return (dispatch) => {
    return dispatch({
      type: 'SHOW_CHAT_DRAWER',
      showDrawer: shouldShow,
    });
  };
}

export function getChatWindowMessages(conversation_id) {
  return (dispatch) => {
    dispatch(requestMessages());
    return {
      payload: apiGet(
        Auth.getApiUrl() + `/chat/conversations/${conversation_id}/chat_window_messages`
      )
        .then((response) => {
          let formattedMessages = [];
          if (response && response.length > 0) {
            formattedMessages = response.map((message) => {
              return formatMessage(message);
            });
          }
          dispatch(receiveMessages(formattedMessages));
        })
        .catch((error) => {
          console.error('request failed', error);
        }),
    };
  };
}

// Support
export function getLandlordDetails() {
  return (dispatch) => {
    apiGet(Auth.getApiUrl() + '/leads/landlord_details')
      .then((response) => {
        dispatch({
          type: 'LANDLORD_DETAILS',
          landlordDetails: response,
        });
      })
      .catch((error) => {
        console.error('request failed', error);
      });
  };
}

export function getProperties() {
  return (dispatch) => {
    apiGet(Auth.getApiUrl() + '/landlords/list_all_properties')
      .then((response) => {
        dispatch({
          type: 'LANDLORD_PROPERTIES',
          properties: response,
        });
      })
      .catch((error) => {
        console.error('request failed', error);
      });
  };
}

export function createPropertyNotification(property_ids, message, file) {
  return (dispatch) => {
    if (!file) {
      apiPost(Auth.getApiUrl() + '/chat/property_notification', {
        property_ids,
        message,
      });
    } else {
      let formData = new FormData();
      formData.append('attachment', file);
      formData.append('message', message);
      formData.append('property_ids', JSON.stringify(property_ids));
      apiPostMultipart(Auth.getApiUrl() + '/chat/property_notification', formData);
    }
  };
}
